import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, markQuizCompleted, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'deap-activite8';

const DEAPActivite8Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const savedProgress = getActivityProgress(ACTIVITY_ID);
    if (savedProgress && savedProgress.lastViewedSlide > 0) {
      // Resume from last position optionally
    }
  }, []);

  const slides = [
    // Slide 1 - Titre
    {
      id: 1,
      type: 'title',
      title: "Activité 8",
      subtitle: "Entretien des locaux et des matériels liés aux activités de soins",
      description: "DEAP – Prévention du risque infectieux et posture professionnelle"
    },
    // Slide 2 - Sommaire
    {
      id: 2,
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "I", title: "Enjeux et cadre professionnel" },
        { num: "II", title: "Principes fondamentaux d'organisation" },
        { num: "III", title: "Techniques professionnelles de nettoyage" },
        { num: "IV", title: "Les produits d'entretien et le bio-nettoyage" },
        { num: "V", title: "Prévention du risque infectieux" },
        { num: "VI", title: "Sécurité des professionnels" },
        { num: "VII", title: "Organisation matérielle" },
        { num: "VIII", title: "Spécificités en petite enfance" },
        { num: "IX", title: "Traçabilité et démarche qualité" },
        { num: "X", title: "Positionnement professionnel" }
      ]
    },
    // SECTION I - Enjeux et cadre professionnel
    {
      id: 3,
      type: 'section',
      section: "I",
      title: "Enjeux et cadre professionnel"
    },
    {
      id: 4,
      type: 'content',
      title: "L'entretien : un acte de soin indirect",
      content: "L'entretien des locaux et du matériel liés aux soins constitue un acte de soin indirect.",
      bullets: [
        "Participe à la prévention des infections",
        "Assure la sécurité des enfants et des professionnels",
        "Contribue à la qualité de l'accueil",
        "Permet la maîtrise du risque infectieux"
      ]
    },
    {
      id: 5,
      type: 'warning',
      title: "Risques d'un défaut d'entretien",
      content: "Un défaut d'entretien peut favoriser :",
      bullets: [
        "La transmission manuportée",
        "La contamination des surfaces",
        "La propagation épidémique",
        "Les infections nosocomiales"
      ],
      warningColor: "#EF4444"
    },
    {
      id: 6,
      type: 'content',
      title: "Risques spécifiques en pédiatrie",
      content: "En pédiatrie et en structure d'accueil, les risques concernent notamment :",
      bullets: [
        "Gastro-entérites",
        "Bronchiolites",
        "Conjonctivites",
        "Infections cutanées"
      ]
    },
    // SECTION II - Principes fondamentaux
    {
      id: 7,
      type: 'section',
      section: "II",
      title: "Principes fondamentaux d'organisation du nettoyage"
    },
    {
      id: 8,
      type: 'principles',
      title: "Les règles d'or du nettoyage",
      content: "L'entretien repose sur une méthodologie rigoureuse :",
      principles: [
        { icon: "1", text: "Nettoyer du plus propre vers le plus sale" },
        { icon: "2", text: "Nettoyer du haut vers le bas" },
        { icon: "3", text: "Progresser du fond de la pièce vers la sortie" },
        { icon: "4", text: "Installer un panneau 'sol mouillé'" },
        { icon: "5", text: "Aérer la pièce avant et après intervention" }
      ],
      note: "Ces règles permettent de limiter les contaminations croisées et de garantir la sécurité."
    },
    // SECTION III - Techniques professionnelles
    {
      id: 9,
      type: 'section',
      section: "III",
      title: "Techniques professionnelles de nettoyage"
    },
    {
      id: 10,
      type: 'technique',
      title: "La technique du détourage",
      content: "Le détourage consiste à nettoyer en priorité :",
      bullets: [
        "Les plinthes",
        "Les angles",
        "Les bordures"
      ],
      benefit: "Il permet d'éviter l'accumulation de salissures et d'assurer une finition professionnelle."
    },
    {
      id: 11,
      type: 'imageSlide',
      title: "La technique de la godille",
      content: "La godille correspond à un mouvement en S ou en huit.",
      image: "/images/technique_godille.png",
      imageAlt: "Technique de la godille - mouvement en S",
      bullets: [
        "Une couverture complète de la surface",
        "Une efficacité mécanique optimale",
        "Une limitation des contaminations croisées"
      ],
      note: "Le matériel ne doit pas quitter le sol durant le mouvement."
    },
    // SECTION IV - Produits d'entretien
    {
      id: 12,
      type: 'section',
      section: "IV",
      title: "Les produits d'entretien et le bio-nettoyage"
    },
    {
      id: 13,
      type: 'products',
      title: "Les familles de produits",
      products: [
        { name: "Détergents", desc: "Éliminent les salissures visibles. Ne détruisent pas les micro-organismes.", color: "#3B82F6" },
        { name: "Désinfectants", desc: "Détruisent ou inactivent les micro-organismes. S'utilisent sur surface propre.", color: "#10B981" },
        { name: "Détergents-désinfectants", desc: "Associent nettoyage et désinfection en une étape.", color: "#8B5CF6" },
        { name: "Détartrants", desc: "Éliminent le tartre favorisant l'adhérence bactérienne.", color: "#F59E0B" }
      ]
    },
    {
      id: 14,
      type: 'content',
      title: "Le bio-nettoyage",
      content: "Le bio-nettoyage associe :",
      bullets: [
        "Action mécanique",
        "Action chimique",
        "Parfois action thermique"
      ],
      note: "Recommandé dans les zones à risque."
    },
    {
      id: 15,
      type: 'warning',
      title: "Importance de la dilution",
      content: "Une dilution incorrecte peut :",
      bullets: [
        "Diminuer l'efficacité du produit",
        "Augmenter la toxicité",
        "Causer des irritations",
        "Laisser des résidus dangereux"
      ],
      warningColor: "#F59E0B"
    },
    // SECTION V - Prévention du risque infectieux
    {
      id: 16,
      type: 'section',
      section: "V",
      title: "Prévention du risque infectieux"
    },
    {
      id: 17,
      type: 'content',
      title: "Les salissures croisées",
      content: "Transfert de micro-organismes d'une zone à une autre.",
      subtitle: "Prévention :",
      bullets: [
        "Changer régulièrement de lavette",
        "Respecter le code couleur",
        "Ne jamais replonger un bandeau sale",
        "Respecter le circuit propre / sale"
      ]
    },
    {
      id: 18,
      type: 'imageSlide',
      title: "Les DASRI",
      subtitle: "Déchets d'Activités de Soins à Risques Infectieux",
      image: "/images/dasri_container.png",
      imageAlt: "Conteneur DASRI jaune",
      bullets: [
        "Compresses souillées",
        "Matériel piquant ou coupant",
        "Déchets contaminés"
      ],
      note: "Ils doivent être éliminés dans des collecteurs homologués selon la filière réglementée."
    },
    {
      id: 19,
      type: 'content',
      title: "Gestion d'un épisode épidémique",
      content: "Renforcement des protocoles :",
      bullets: [
        "Désinfection accrue",
        "Aération renforcée",
        "Traçabilité des surfaces",
        "Isolement si nécessaire"
      ]
    },
    // SECTION VI - Sécurité des professionnels
    {
      id: 20,
      type: 'section',
      section: "VI",
      title: "Sécurité des professionnels et gestion des risques"
    },
    {
      id: 21,
      type: 'content',
      title: "Équipements de protection",
      bullets: [
        "Port de gants adapté",
        "Blouse si nécessaire",
        "Hygiène des mains après retrait"
      ],
      highlight: "Les gants ne remplacent jamais le lavage des mains !"
    },
    {
      id: 22,
      type: 'warning',
      title: "Risques chimiques",
      content: "Les produits d'entretien peuvent causer :",
      bullets: [
        "Irritations cutanées",
        "Troubles respiratoires",
        "Allergies",
        "Brûlures"
      ],
      rules: [
        "Lire les pictogrammes CLP",
        "Ne jamais mélanger les produits",
        "Stocker hors de portée des enfants"
      ],
      warningColor: "#DC2626"
    },
    // SECTION VII - Organisation matérielle
    {
      id: 23,
      type: 'section',
      section: "VII",
      title: "Organisation matérielle et logistique"
    },
    {
      id: 24,
      type: 'imageSlide',
      title: "Le chariot de ménage",
      image: "/images/chariot_menage.png",
      imageAlt: "Chariot de ménage professionnel avec double seau",
      content: "Organisation du chariot :",
      bullets: [
        "Zone propre séparée",
        "Zone sale identifiée",
        "Produits rangés",
        "Matériel organisé",
        "Code couleur recommandé"
      ]
    },
    {
      id: 25,
      type: 'content',
      title: "Le circuit du linge",
      content: "Distinguer :",
      bullets: [
        "Linge propre",
        "Linge sale",
        "Linge contaminé"
      ],
      note: "Respect strict du circuit propre / sale."
    },
    {
      id: 26,
      type: 'content',
      title: "Hygiène des jouets",
      bullets: [
        "Nettoyage régulier",
        "Désinfection en cas de mise à la bouche",
        "Peluches lavables uniquement"
      ]
    },
    // SECTION VIII - Spécificités petite enfance
    {
      id: 27,
      type: 'section',
      section: "VIII",
      title: "Spécificités en petite enfance"
    },
    {
      id: 28,
      type: 'content',
      title: "Environnement adapté aux enfants",
      content: "L'environnement doit être :",
      bullets: [
        "Non toxique",
        "Sans résidus irritants",
        "Sans odeur agressive"
      ],
      note: "Le rinçage peut être nécessaire pour éviter tout résidu chimique."
    },
    // SECTION IX - Traçabilité
    {
      id: 29,
      type: 'section',
      section: "IX",
      title: "Traçabilité et démarche qualité"
    },
    {
      id: 30,
      type: 'content',
      title: "Outils de traçabilité",
      bullets: [
        "Fiches de traçabilité",
        "Grilles de nettoyage",
        "Protocoles écrits",
        "Transmission en cas de risque infectieux"
      ],
      note: "L'auxiliaire de puériculture s'inscrit dans une démarche institutionnelle de qualité et de sécurité."
    },
    // SECTION X - Positionnement professionnel
    {
      id: 31,
      type: 'section',
      section: "X",
      title: "Positionnement professionnel et analyse réflexive"
    },
    {
      id: 32,
      type: 'competences',
      title: "Compétences DEAP",
      content: "Dans le cadre du DEAP, l'auxiliaire de puériculture démontre sa capacité à :",
      competences: [
        "Organiser son intervention",
        "Appliquer une méthodologie rigoureuse",
        "Prévenir les infections",
        "Adapter son action à la situation",
        "Collaborer avec l'équipe"
      ],
      conclusion: "L'entretien devient un véritable acte professionnel au service de la sécurité sanitaire."
    },
    // Conclusion
    {
      id: 33,
      type: 'conclusion',
      title: "Conclusion",
      content: "L'activité 8 repose sur :",
      bullets: [
        "Une méthode rigoureuse",
        "Des techniques précises (détourage, godille)",
        "Une connaissance des produits",
        "La prévention des contaminations croisées",
        "La gestion des DASRI",
        "La maîtrise du risque infectieux",
        "Une posture professionnelle responsable"
      ],
      finalNote: "Elle positionne l'auxiliaire de puériculture comme un acteur essentiel de la qualité et de la sécurité des soins."
    }
  ];

  const quizQuestions = [
    { id: 1, question: "Pourquoi nettoie-t-on du plus propre vers le plus sale ?", options: ["Pour gagner du temps", "Pour éviter les contaminations croisées", "Pour utiliser moins de produit"], correct: 1 },
    { id: 2, question: "Pourquoi progresse-t-on du fond de la pièce vers la sortie ?", options: ["Pour aérer plus vite", "Pour ne pas recontaminer la zone nettoyée", "Pour éviter le bruit"], correct: 1 },
    { id: 3, question: "La technique de la godille correspond à :", options: ["Un mouvement circulaire", "Un mouvement en S ou en huit", "Un mouvement rectiligne"], correct: 1 },
    { id: 4, question: "Le détourage consiste à :", options: ["Nettoyer le centre de la pièce", "Nettoyer les angles et plinthes en priorité", "Désinfecter les sanitaires"], correct: 1 },
    { id: 5, question: "Un détergent permet :", options: ["De tuer les virus", "D'éliminer les salissures visibles", "De stériliser le matériel"], correct: 1 },
    { id: 6, question: "Un désinfectant doit être utilisé :", options: ["Sur une surface sale", "Après nettoyage", "Sans respecter de temps de contact"], correct: 1 },
    { id: 7, question: "Le bio-nettoyage associe :", options: ["Eau et savon uniquement", "Action mécanique et chimique", "Balayage à sec uniquement"], correct: 1 },
    { id: 8, question: "Les DASRI concernent :", options: ["Les déchets alimentaires", "Les déchets ménagers classiques", "Les déchets à risque infectieux"], correct: 2 },
    { id: 9, question: "Les gants remplacent-ils le lavage des mains ?", options: ["Oui, complètement", "Non, jamais"], correct: 1 },
    { id: 10, question: "Pourquoi installe-t-on un panneau 'sol mouillé' ?", options: ["Pour respecter le protocole", "Pour prévenir les chutes", "Pour signaler la fin du nettoyage"], correct: 1 },
    { id: 11, question: "Une dilution incorrecte peut :", options: ["Améliorer l'efficacité", "Diminuer l'efficacité ou augmenter la toxicité", "Être sans conséquence"], correct: 1 },
    { id: 12, question: "Les salissures croisées correspondent :", options: ["À un nettoyage incomplet", "Au transfert de micro-organismes d'une zone à une autre", "À un défaut de matériel"], correct: 1 },
    { id: 13, question: "Le tartre favorise :", options: ["La propreté", "L'adhérence des bactéries", "La désinfection naturelle"], correct: 1 },
    { id: 14, question: "En cas d'épidémie, il faut :", options: ["Réduire les nettoyages", "Renforcer la désinfection", "Supprimer l'aération"], correct: 1 },
    { id: 15, question: "Les jouets en crèche doivent être :", options: ["Nettoyés uniquement en fin d'année", "Nettoyés régulièrement et désinfectés si souillés", "Nettoyés uniquement s'ils tombent"], correct: 1 },
    { id: 16, question: "Pourquoi aérer une pièce après nettoyage ?", options: ["Pour faire entrer du froid", "Pour renouveler l'air et évacuer les émanations", "Pour sécher plus vite"], correct: 1 },
    { id: 17, question: "Le circuit du linge doit :", options: ["Mélanger propre et sale", "Séparer propre et sale", "Être facultatif"], correct: 1 },
    { id: 18, question: "Peut-on mélanger deux produits d'entretien ?", options: ["Oui, pour plus d'efficacité", "Non, c'est dangereux"], correct: 1 },
    { id: 19, question: "Une infection nosocomiale est :", options: ["Une infection contractée en collectivité", "Une infection contractée lors d'une prise en charge en établissement", "Une infection alimentaire"], correct: 1 },
    { id: 20, question: "L'entretien des locaux est :", options: ["Une tâche secondaire", "Un acte de soin indirect", "Une obligation administrative uniquement"], correct: 1 }
  ];

  const API_URL = window.location.origin || '';

  const transformTextForTTS = (text) => {
    if (!text) return '';
    let transformed = text
      .replace(/\bAP\b/g, 'auxiliaire de puériculture')
      .replace(/\bDEAP\b/g, 'diplôme d\'état d\'auxiliaire de puériculture')
      .replace(/\bDASRI\b/g, 'déchets d\'activités de soins à risques infectieux')
      .replace(/\bCLP\b/g, 'classification, étiquetage et emballage');
    return transformed;
  };

  const speakText = async (text) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const transformedText = transformTextForTTS(text);
      const truncatedText = transformedText.substring(0, 800);
      
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: truncatedText, voice: 'nova', speed: 1.1 })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = new Audio(data.audio_url);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        } else if (data.audio_base64) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        }
      } else {
        toast.error("Erreur lors de la génération audio");
      }
    } catch (error) {
      toast.error("Erreur de connexion au service audio");
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const getSlideText = (slide) => {
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.content) text += '. ' + slide.content;
    if (slide.bullets) text += '. ' + slide.bullets.join('. ');
    if (slide.note) text += '. ' + slide.note;
    if (slide.highlight) text += '. ' + slide.highlight;
    return text;
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      markSlideViewed(ACTIVITY_ID, newSlide, slides.length);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    markSlideViewed(ACTIVITY_ID, index, slides.length);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
    markQuizCompleted(ACTIVITY_ID, score, quizQuestions.length);
    toast.success(`Quiz terminé ! Score: ${score}/${quizQuestions.length}`);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-8" style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)' }}>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl">{slide.subtitle}</p>
            <p className="text-lg text-white/80 mt-4">{slide.description}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 100%)' }}>
            <h2 className="text-3xl font-bold text-emerald-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-emerald-100 cursor-pointer hover:bg-emerald-50 transition-colors"
                  onClick={() => goToSlide(idx * 3 + 2)}
                >
                  <span className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {section.num}
                  </span>
                  <span className="text-gray-700 text-sm">{section.title}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full" style={{ background: 'linear-gradient(135deg, #059669 0%, #34D399 100%)' }}>
            <div className="text-6xl font-bold text-white/30 mb-4">{slide.section}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center px-8">{slide.title}</h2>
          </div>
        );

      case 'content':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.subtitle && <p className="text-emerald-700 font-medium mb-3">{slide.subtitle}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.highlight && (
              <div className="mt-4 p-4 bg-amber-100 border-l-4 border-amber-500 rounded">
                <p className="text-amber-800 font-medium">{slide.highlight}</p>
              </div>
            )}
            {slide.note && (
              <p className="mt-4 text-sm text-emerald-600 italic">{slide.note}</p>
            )}
          </div>
        );

      case 'warning':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: `linear-gradient(180deg, ${slide.warningColor}10 0%, #FFFFFF 100%)` }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: slide.warningColor }}>{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.bullets && (
              <ul className="space-y-2 mb-4">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <XCircle size={20} style={{ color: slide.warningColor }} className="flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.rules && (
              <div className="mt-4 p-4 bg-white rounded-lg border-2" style={{ borderColor: slide.warningColor }}>
                <p className="font-medium mb-2">Règles de sécurité :</p>
                <ul className="space-y-1">
                  {slide.rules.map((rule, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-green-500" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'principles':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">{slide.title}</h2>
            <p className="text-gray-700 mb-4">{slide.content}</p>
            <div className="space-y-3">
              {slide.principles.map((p, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 bg-white rounded-lg shadow-sm border border-emerald-100">
                  <span className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold">
                    {p.icon}
                  </span>
                  <span className="text-gray-700">{p.text}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-emerald-600 italic">{slide.note}</p>
          </div>
        );

      case 'technique':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">{slide.title}</h2>
            <p className="text-gray-700 mb-4">{slide.content}</p>
            {slide.bullets && (
              <ul className="space-y-2 mb-4">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-emerald-700">{slide.benefit}</p>
            </div>
          </div>
        );

      case 'imageSlide':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-emerald-800 mb-2">{slide.title}</h2>
            {slide.subtitle && <p className="text-emerald-600 mb-3">{slide.subtitle}</p>}
            {slide.content && <p className="text-gray-700 mb-3">{slide.content}</p>}
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="md:w-1/2">
                <img 
                  src={slide.image} 
                  alt={slide.imageAlt}
                  className="w-full max-h-64 object-contain rounded-lg shadow-md bg-white p-2"
                />
              </div>
              <div className="md:w-1/2">
                {slide.bullets && (
                  <ul className="space-y-2">
                    {slide.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {slide.note && (
              <p className="mt-4 text-sm text-emerald-600 italic bg-emerald-50 p-3 rounded">{slide.note}</p>
            )}
          </div>
        );

      case 'products':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-emerald-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {slide.products.map((product, idx) => (
                <div key={idx} className="p-4 bg-white rounded-lg shadow-sm border-l-4" style={{ borderColor: product.color }}>
                  <h3 className="font-bold mb-2" style={{ color: product.color }}>{product.name}</h3>
                  <p className="text-gray-600 text-sm">{product.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'competences':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">{slide.title}</h2>
            <p className="text-gray-700 mb-4">{slide.content}</p>
            <div className="space-y-3">
              {slide.competences.map((comp, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-emerald-100">
                  <CheckCircle className="text-emerald-500" size={20} />
                  <span className="text-gray-700">{comp}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-emerald-100 rounded-lg">
              <p className="text-emerald-800 font-medium">{slide.conclusion}</p>
            </div>
          </div>
        );

      case 'conclusion':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)' }}>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">{slide.title}</h2>
            <p className="text-white/90 mb-4 text-center">{slide.content}</p>
            <div className="max-w-2xl mx-auto">
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white">
                    <CheckCircle size={20} className="text-emerald-200" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 p-4 bg-white/20 rounded-lg max-w-2xl mx-auto">
              <p className="text-white text-center">{slide.finalNote}</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-8 h-full flex items-center justify-center">
            <p className="text-gray-500">Contenu non disponible</p>
          </div>
        );
    }
  };

  if (showQuiz) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold text-emerald-800 mb-6">Quiz - Activité 8</h2>
        <p className="text-gray-600 mb-6">Testez vos connaissances sur l'entretien des locaux et matériels de soins.</p>
        
        <div className="space-y-6">
          {quizQuestions.map((q, idx) => (
            <div key={q.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <p className="font-medium text-gray-800 mb-3">{idx + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((option, optIdx) => (
                  <button
                    key={optIdx}
                    onClick={() => handleQuizAnswer(q.id, optIdx)}
                    disabled={quizSubmitted}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      quizSubmitted
                        ? optIdx === q.correct
                          ? 'bg-green-100 border-green-500'
                          : quizAnswers[q.id] === optIdx
                            ? 'bg-red-100 border-red-500'
                            : 'bg-gray-50 border-gray-200'
                        : quizAnswers[q.id] === optIdx
                          ? 'bg-emerald-100 border-emerald-500'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {quizSubmitted && optIdx === q.correct && <CheckCircle size={18} className="text-green-600" />}
                      {quizSubmitted && quizAnswers[q.id] === optIdx && optIdx !== q.correct && <XCircle size={18} className="text-red-600" />}
                      {option}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!quizSubmitted ? (
          <button
            onClick={submitQuiz}
            disabled={Object.keys(quizAnswers).length < quizQuestions.length}
            className="mt-6 w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Valider le quiz ({Object.keys(quizAnswers).length}/{quizQuestions.length} réponses)
          </button>
        ) : (
          <div className="mt-6 p-6 bg-emerald-50 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-emerald-800">Score : {quizScore}/{quizQuestions.length}</h3>
            <p className="text-emerald-600 mt-2">
              {quizScore >= 16 ? "Excellent ! Vous maîtrisez le sujet." :
               quizScore >= 12 ? "Bon travail ! Quelques points à revoir." :
               "Continuez à étudier pour améliorer vos connaissances."}
            </p>
            <button
              onClick={() => { setShowQuiz(false); setCurrentSlide(0); setQuizAnswers({}); setQuizSubmitted(false); }}
              className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Revoir la présentation
            </button>
          </div>
        )}
      </div>
    );
  }

  const slide = slides[currentSlide];

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="h-1 bg-gray-200">
        <div 
          className="h-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide content */}
      <div className="flex-1 overflow-hidden">
        {renderSlide(slide)}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-4 bg-white border-t">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX size={18} />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 size={18} />
                Écouter
              </>
            )}
          </button>
        </div>

        <button
          onClick={nextSlide}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default DEAPActivite8Presentation;
